// sidebar.component.ts
import { Component, OnInit } from '@angular/core';
import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { MenuItem, MENU_ITEMS } from './sidebar-data';
import { CommonModule } from '@angular/common';
import { MatSidenavModule } from '@angular/material/sidenav';
import { RouterModule, Router } from '@angular/router';
import { MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SidebarService } from 'src/app/services/side-bar.Service';
import { environment } from 'src/environments/environment';
import { UserService } from 'src/app/services/api/user.service';
import { AccessLevel, PermissionsService } from 'src/app/services/permissions.service';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  imports: [
    CommonModule,
    MatSidenavModule,
    RouterModule,
    MatListModule,
    MatIconModule,
    MatButtonModule,
    RouterModule,
    MatTooltipModule,
  ],
  styleUrls: ['./sidebar.component.scss'],
  animations: [
    trigger('expandCollapse', [
      state(
        'collapsed',
        style({
          width: '64px',
        })
      ),
      state(
        'expanded',
        style({
          width: '222px',
        })
      ),
      transition('expanded <=> collapsed', [animate('200ms ease-in-out')]),
    ]),
    trigger('submenuAnimation', [
      state(
        'closed',
        style({
          height: '0',
          opacity: '0',
        })
      ),
      state(
        'open',
        style({
          height: '*',
          opacity: '1',
        })
      ),
      transition('closed <=> open', [animate('200ms ease-in-out')]),
    ]),
  ],
})
export class SidebarComponent implements OnInit {
  opened = false;
  openedMobile = false;
  isClosing = false;
  menuItems: MenuItem[] = [];
  allMenuItems = MENU_ITEMS;
  expandedItems: Set<string> = new Set();
  version = environment.version;
  currentUserPermission: string | null = null;

  constructor(
    public sidebarService: SidebarService,
    private userService: UserService,
    private permissionsService: PermissionsService,
    private router: Router
  ) {
    this.sidebarService.sidebarOpen$.subscribe(
      (opened: boolean) => (this.opened = opened)
    );

    this.sidebarService.sidebarMobleOpen$.subscribe((opened: boolean) => {
      if (opened) {
        this.openedMobile = true;
        this.isClosing = false;
        this.opened = true;
      }
    });
    
    // Filter menu items based on user permissions
    this.userService.currentUser$.subscribe(user => {
      if (user) {
        this.currentUserPermission = user.permission;
        this.filterMenuItems();
      }
    });
  }

  ngOnInit(): void {
    // Initial filter of menu items
    this.filterMenuItems();
  }
  
  /**
   * Filter menu items based on user's permission level
   */
  filterMenuItems(): void {
    // Create a shallow copy of the menu items
    const filteredItems: MenuItem[] = [];
    
    for (const item of this.allMenuItems) {
      // For top-level items, check route permissions
      const route = this.router.config.find(r => r.path === item.link?.substring(1));
      
      if (!route) {
        // If no matching route (e.g., external links), include it
        filteredItems.push({...item});
        continue;
      }
      
      const permissions = route.data?.['permissions'];
      
      // If no permissions defined, show to everyone
      if (!permissions) {
        filteredItems.push({...item});
        continue;
      }
      
      // Check if user has access to this route
      if (this.currentUserPermission === 'Super-Admin' || 
          permissions[this.currentUserPermission || ''] !== AccessLevel.BLOCKED) {
        
        // For items with subitems, filter the subitems too
        if (item.subItems && item.subItems.length) {
          const filteredSubItems = item.subItems.filter(subItem => {
            const subRoute = this.router.config.find(r => r.path === subItem.link?.substring(1));
            if (!subRoute) return true;
            
            const subPermissions = subRoute.data?.['permissions'];
            if (!subPermissions) return true;
            
            return this.currentUserPermission === 'Super-Admin' || 
                   subPermissions[this.currentUserPermission || ''] !== AccessLevel.BLOCKED;
          });
          
          // Only include parent item if it has visible subitems
          if (filteredSubItems.length) {
            filteredItems.push({
              ...item,
              subItems: filteredSubItems
            });
          }
        } else {
          // Regular item without subitems
          filteredItems.push({...item});
        }
      }
    }
    
    this.menuItems = filteredItems;
  }

  resetActiveItem(): void {
    this.expandedItems.clear();
  }

  toggleSidebar(): void {
    this.sidebarService.toggleSidebar();
  }

  toggleSubmenu(itemTitle: string): void {
    if (this.expandedItems.has(itemTitle)) {
      this.expandedItems.delete(itemTitle);
    } else {
      this.expandedItems.add(itemTitle);
    }
  }

  isSubmenuOpen(itemTitle: string): boolean {
    return this.expandedItems.has(itemTitle);
  }

  closeMobileSidebar(): void {
    this.isClosing = true;
    // Wait for animation to complete before hiding
    setTimeout(() => {
      this.openedMobile = false;
      this.isClosing = false;
      this.sidebarService.toggleMobileSidebar();
    }, 300); // Match animation duration
  }

  handleNavigation(): void {
    if (this.openedMobile) {
      this.closeMobileSidebar();
    }
  }
}
